import { Fragment } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Divider,
  Grid,
  LinearProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { GridView, ViewList } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import UserHint from "../../../../components/display/UserHint";
import { useQuery } from "@tanstack/react-query";
import { fetchAppointment } from "../../../../enums/queryFunction/Fetch";
import SimpleAlert from "../../../../components/display/SimpleAlert";
import { APPOINTMENT_FILES } from "../../../../enums/QueryKeys";
import FileDisplay from "../../../../components/display/FileDisplay";
import AttachFileDialog from "../../../../components/AttachFileDialog";
import {
  ATTACH_FILE_TO_PET_AND_APP,
  FILE_DOWNLOAD,
} from "../../../../enums/ApiPaths";
import FileViewerDialog from "../../../../components/display/fileViewer/FileViewerDialog";
import useFileViewer from "../../../../hooks/useFileViewer";
import useDeleteAppointmentFile from "../../hooks/useDeleteAppointmentFile";
import AppointmentFilesTable from "./AppointmentFilesTable";

// Kept in the URL so the chosen view survives a refresh
const FILES_VIEW_PARAM = "filesView";
const FILES_VIEW = { GRID: "grid", TABLE: "table" };

export default function AppointmentFiles() {
  const { id } = useParams();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading, error } = useQuery({
    queryKey: [APPOINTMENT_FILES + id],
    queryFn: () => fetchAppointment(id, "Documents"),
    staleTime: Infinity,
  });
  const {
    viewFile,
    downloadFile,
    downloadViewerFile,
    closeViewer,
    viewerFile,
    isPathLoading,
  } = useFileViewer();
  const { handleDeleteFile, deletingFileId, canDelete, ConfirmationDialog } =
    useDeleteAppointmentFile(id);
  const uploadUrl = `${ATTACH_FILE_TO_PET_AND_APP}${id}`;
  const files = data;
  const view =
    searchParams.get(FILES_VIEW_PARAM) === FILES_VIEW.TABLE
      ? FILES_VIEW.TABLE
      : FILES_VIEW.GRID;

  function handleViewChange(_, newView) {
    if (!newView) return;
    const newParams = new URLSearchParams(searchParams);
    newParams.set(FILES_VIEW_PARAM, newView);
    setSearchParams(newParams, { replace: true });
  }

  const fileRequest = (file) => ({
    path: `${FILE_DOWNLOAD}${file?.fileId}`,
    name: file?.name,
  });
  const loadingFileId = files?.find(
    (file) =>
      file?.fileId === deletingFileId ||
      isPathLoading(`${FILE_DOWNLOAD}${file?.fileId}`)
  )?.fileId;

  if (isLoading) {
    return <LinearProgress />;
  }
  if (error) {
    return <SimpleAlert message={"SomethingWentWrong"} severity={"error"} />;
  }

  return (
    <Fragment>
      <Stack alignItems={"center"} direction={"row"}>
        {files?.length === 0 ? (
          <SimpleAlert message={"NoDocumentsCurrently"} severity={"info"} />
        ) : (
          <Box sx={{width: "100%"}}>
            <UserHint
              hint={
                view === FILES_VIEW.TABLE
                  ? "ClickOnViewIconToViewFile"
                  : "ClickOnAFileToViewIt"
              }
            />
          </Box>
        )}
        {files?.length > 0 ? (
          <ToggleButtonGroup
            value={view}
            exclusive
            size="small"
            onChange={handleViewChange}
            sx={{ mr: 1, flexShrink: 0 }}
          >
            <ToggleButton
              value={FILES_VIEW.GRID}
              aria-label={t("GridView")}
              data-testid="appointment.details.files.grid-view-toggle"
            >
              <Tooltip title={t("GridView")}>
                <GridView fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton
              value={FILES_VIEW.TABLE}
              aria-label={t("TableView")}
              data-testid="appointment.details.files.table-view-toggle"
            >
              <Tooltip title={t("TableView")}>
                <ViewList fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        ) : null}
        <AttachFileDialog endpoint={uploadUrl}/>
      </Stack>
      {files?.length > 0 ?
        <>
          <Divider sx={{mt:4, mb: 4}}/>
          {view === FILES_VIEW.TABLE ? (
            <AppointmentFilesTable
              files={files}
              canDelete={canDelete}
              loadingFileId={loadingFileId}
              onView={(file) => viewFile(fileRequest(file))}
              onDownload={(file) => downloadFile(fileRequest(file))}
              onDelete={(file) => handleDeleteFile(file?.fileId, file?.name)}
            />
          ) : (
            <Grid container mt={4}>
              {files?.map((file) => (
                <FileDisplay
                  key={file?.fileId}
                  name={file?.name}
                  isLoading={loadingFileId === file?.fileId}
                  canDelete={canDelete}
                  onView={() => viewFile(fileRequest(file))}
                  onDownload={() => downloadFile(fileRequest(file))}
                  onDelete={() => handleDeleteFile(file?.fileId, file?.name)}
                />
              ))}
            </Grid>
          )}
        </>
      : null}
      <ConfirmationDialog />
      <FileViewerDialog
        file={viewerFile}
        onClose={closeViewer}
        onDownload={downloadViewerFile}
      />
    </Fragment>
  );
}
